import { usePISPAuthenticate } from "Api/Mutations/Transactions/usePISPAuthenticate";
import { useTransactionQuickPay } from "Api/Mutations/Transactions/useTransactionQuickPay";
import type { BankEnvironment } from "Infrastructure/Api/Api";
import { useState, useEffect } from "react";
import { useResource } from "Translations/Resources";
import { getQuickPaymentAttemptedKey } from "Utils/Storage/LocalStorage/LocalStorageUtils";
import { setLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import {
  type ErrorType,
  handleTransactionPayError,
} from "Utils/TransactionErrorsUtils";

export const useTriggerQuickPayment = (
  transactionPublicID: string,
  environment: BankEnvironment,
) => {
  const [error, setError] = useState<string | null>(null);
  // Server refused quick pay outright ("quickPaymentDisabled": tip-enabled pay point, no eligible
  // primary account). Not a payment failure — the caller should hand over to the standard form.
  const [isQuickPaymentUnavailable, setIsQuickPaymentUnavailable] =
    useState(false);

  const { mutateAsync: quickPayTransaction, isPending } =
    useTransactionQuickPay();
  const { mutate: fetchAndRedirectToPISPURL } = usePISPAuthenticate();
  const { t } = useResource();

  useEffect(() => {
    const quickPaymentAttemptedStorageKey =
      getQuickPaymentAttemptedKey(transactionPublicID);

    const triggerPayment = async () => {
      const payload = { publicID: transactionPublicID };

      try {
        setLocalStorageItem(quickPaymentAttemptedStorageKey, "true");
        await quickPayTransaction(payload, {
          onError: (error) => {
            const isPISPError = (error as unknown as ErrorType).data?.errors
              ?.pispAuthentication;

            if (isPISPError) {
              fetchAndRedirectToPISPURL({
                environment,
                transactionPublicID,
              });
            }
          },
        });
      } catch (error) {
        const typedError = error as ErrorType;
        const isQuickPaymentDisabled =
          !!typedError.data?.errors?.quickPaymentDisabled;

        if (isQuickPaymentDisabled) {
          // Only reachable when the client's own guards are stale (e.g. a cached bundle inside the
          // iOS shell). Fall back silently instead of surfacing an "unknown error" (ZS-1674).
          setIsQuickPaymentUnavailable(true);
          return;
        }

        const errorMessage = handleTransactionPayError(typedError, t, false);
        setError(errorMessage ?? null);
      }
    };

    triggerPayment();
  }, [
    environment,
    transactionPublicID,
    quickPayTransaction,
    fetchAndRedirectToPISPURL,
    t,
  ]);

  return { error, isPending, isQuickPaymentUnavailable };
};
